/**
 * Monthlees modal overlay — triggers, multi-step goal quiz, and email opt-in.
 */
(function () {
  'use strict';

  function capture(name, props) {
    if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') {
      window.MonthleesAnalytics.track(name, props || {});
      return;
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(name, props || {});
    }
  }

  function parseTags(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(function (tag) {
        return tag.trim();
      })
      .filter(Boolean);
  }

  function uniqueTags(tags) {
    var seen = {};
    return tags.filter(function (tag) {
      if (seen[tag]) return false;
      seen[tag] = true;
      return true;
    });
  }

  function joinTags(tags) {
    return uniqueTags(tags).join(', ');
  }

  function extractFormErrors(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var errorNodes = doc.querySelectorAll('.errors li, .form-message--error, .note--error');

    if (errorNodes.length === 0) {
      return [];
    }

    return Array.from(errorNodes)
      .map(function (node) {
        return node.textContent.trim();
      })
      .filter(Boolean);
  }

  function formLooksSuccessful(html, errors) {
    if (errors.length > 0) {
      return false;
    }

    return (
      html.indexOf('form-success') !== -1 ||
      html.indexOf('customer_posted') !== -1 ||
      html.indexOf('posted_successfully') !== -1 ||
      html.indexOf('thank-you') !== -1 ||
      html.indexOf('Thank you') !== -1
    );
  }

  function initModal(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    var dialog = root.querySelector('[data-mmo-dialog]');
    var closeEls = root.querySelectorAll('[data-mmo-close]');
    var step1 = root.querySelector('[data-mmo-step="1"]');
    var step2 = root.querySelector('[data-mmo-step="2"]');
    var backButton = root.querySelector('[data-mmo-back]');
    var form = root.querySelector('.monthlees-modal-overlay__form');
    var tagsInput = root.querySelector('[data-mmo-tags-input]');
    var emailInput = root.querySelector('[data-mmo-email-input]');
    var submitButton = root.querySelector('[data-mmo-submit]');
    var feedback = root.querySelector('[data-mmo-form-feedback]');
    var formFields = root.querySelector('[data-mmo-form-fields]');
    var step1Heading = step1 ? step1.querySelector('.monthlees-modal-overlay__heading') : null;
    var step2Heading = step2 ? step2.querySelector('.monthlees-modal-overlay__heading') : null;
    var storageKey = root.dataset.storageKey || 'monthlees-modal-overlay';
    var frequency = root.dataset.showFrequency || 'session';
    var persistDays = parseInt(root.dataset.persistDays || '7', 10);
    var exitEnabled = root.dataset.triggerExit === 'true';
    var timeEnabled = root.dataset.triggerTime === 'true';
    var timeDelay = parseInt(root.dataset.triggerTimeMs || '5000', 10);
    var scrollEnabled = root.dataset.triggerScroll === 'true';
    var scrollPercent = parseInt(root.dataset.triggerScrollPct || '50', 10);
    var designMode = root.dataset.designMode === 'true';
    var bypassFrequency = root.dataset.bypassFrequency === 'true';
    var baseTags = parseTags(root.dataset.baseTags || 'modal_quiz,newsletter');
    var successMessage = root.dataset.step2Success || 'Thanks — you are all set.';
    var opened = false;
    var armed = true;
    var currentStep = 1;
    var selectedPreferenceTag = '';
    var selectedRedirect = '';
    var returnFocusEl = null;
    var FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Presentation hook for the glass skin: '' | 'loading' | 'error' | 'success'.
    function setState(state) {
      if (state) {
        root.dataset.mmoState = state;
      } else {
        delete root.dataset.mmoState;
      }
      if (form) {
        if (state === 'loading') {
          form.setAttribute('aria-busy', 'true');
        } else {
          form.removeAttribute('aria-busy');
        }
      }
    }

    function markSelected(button) {
      root.querySelectorAll('[data-mmo-select]').forEach(function (el) {
        el.classList.toggle('monthlees-modal-overlay__option--selected', el === button);
      });
    }

    function focusableInDialog() {
      if (!dialog) return [];
      return Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return !el.closest('[hidden]') && el.getClientRects().length > 0;
      });
    }

    function trapFocus(event) {
      if (event.key !== 'Tab' || root.hidden) return;
      var items = focusableInDialog();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    function onDocumentKeydown(event) {
      if (root.hidden) return;
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      trapFocus(event);
    }

    function isStorageBlocked() {
      if (designMode || bypassFrequency || frequency === 'always') return false;

      if (frequency === 'session') {
        return window.sessionStorage.getItem(storageKey) === '1';
      }

      if (frequency === 'persistent') {
        var raw = window.localStorage.getItem(storageKey);
        if (!raw) return false;
        var expiry = parseInt(raw, 10);
        return !Number.isNaN(expiry) && Date.now() < expiry;
      }

      return false;
    }

    function markShown() {
      if (designMode || bypassFrequency || frequency === 'always') return;

      if (frequency === 'session') {
        window.sessionStorage.setItem(storageKey, '1');
      } else if (frequency === 'persistent') {
        var expiry = Date.now() + persistDays * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(storageKey, String(expiry));
      }
    }

    function lockScroll() {
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
    }

    function unlockScroll() {
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }

    function resetFeedback() {
      setState('');
      if (!feedback) return;
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.classList.remove(
        'monthlees-modal-overlay__form-feedback--error',
        'monthlees-modal-overlay__form-feedback--success'
      );
    }

    function showFeedback(type, message) {
      setState(type);
      if (!feedback) return;
      feedback.hidden = false;
      feedback.textContent = message;
      feedback.classList.toggle('monthlees-modal-overlay__form-feedback--error', type === 'error');
      feedback.classList.toggle('monthlees-modal-overlay__form-feedback--success', type === 'success');
    }

    function updateTagsInput() {
      if (!tagsInput) return;
      var tags = baseTags.slice();
      if (selectedPreferenceTag) {
        tags.push(selectedPreferenceTag);
      }
      tagsInput.value = joinTags(tags);
    }

    function setStep(step) {
      currentStep = step;

      if (step1) {
        step1.hidden = step !== 1;
      }

      if (step2) {
        step2.hidden = step !== 2;
      }

      if (dialog) {
        if (step === 2 && step2Heading && step2Heading.id) {
          dialog.setAttribute('aria-labelledby', step2Heading.id);
        } else if (step1Heading && step1Heading.id) {
          dialog.setAttribute('aria-labelledby', step1Heading.id);
        }
      }

      if (step === 2 && emailInput) {
        window.requestAnimationFrame(function () {
          emailInput.focus();
        });
      }
    }

    function resetModalState() {
      selectedPreferenceTag = '';
      selectedRedirect = '';
      markSelected(null);
      resetFeedback();
      updateTagsInput();

      if (form) {
        form.reset();
      }

      if (formFields) {
        formFields.hidden = false;
      }

      if (submitButton) {
        submitButton.disabled = false;
      }

      setStep(1);
    }

    function openModal(trigger) {
      if (!dialog || opened || !armed) return;
      opened = true;
      armed = false;
      markShown();
      if (!designMode) {
        capture('modal_shown', { trigger: trigger || 'unknown', frequency: frequency });
      }
      resetModalState();
      root.hidden = false;
      root.removeAttribute('hidden');
      root.setAttribute('aria-hidden', 'false');
      lockScroll();
      returnFocusEl = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
      document.addEventListener('keydown', onDocumentKeydown, true);

      var focusTarget = root.querySelector('.monthlees-modal-overlay__close') || root.querySelector('[data-mmo-close]');
      if (focusTarget) {
        window.requestAnimationFrame(function () {
          focusTarget.focus();
        });
      }
    }

    function closeModal() {
      if (!dialog || root.hidden) return;
      root.hidden = true;
      root.setAttribute('hidden', '');
      root.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', onDocumentKeydown, true);
      unlockScroll();
      resetModalState();
      if (returnFocusEl && document.contains(returnFocusEl) && typeof returnFocusEl.focus === 'function') {
        returnFocusEl.focus({ preventScroll: true });
      }
      returnFocusEl = null;
    }

    function tryOpen(trigger) {
      if (isStorageBlocked()) {
        armed = false;
        return;
      }
      openModal(trigger);
    }

    function goToStep2(button) {
      selectedPreferenceTag = button.dataset.mmoTag || '';
      selectedRedirect = button.dataset.mmoRedirect || '';
      if (!designMode) {
        capture('modal_goal_selected', { goal_tag: selectedPreferenceTag || 'none' });
      }
      updateTagsInput();
      resetFeedback();
      markSelected(button);
      setStep(2);
    }

    function finishSuccess() {
      if (!designMode) {
        capture('modal_email_submitted', { goal_tag: selectedPreferenceTag || 'none' });
      }
      showFeedback('success', successMessage);

      if (formFields) {
        formFields.hidden = true;
      }

      window.setTimeout(function () {
        closeModal();
        if (selectedRedirect) {
          window.location.href = selectedRedirect;
        }
      }, 1200);
    }

    function submitEmail(event) {
      event.preventDefault();
      if (!form || !submitButton) return;

      resetFeedback();
      updateTagsInput();
      submitButton.disabled = true;
      setState('loading');

      fetch('/contact', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html,application/xhtml+xml',
        },
      })
        .then(function (response) {
          return response.text().then(function (html) {
            return { html: html, ok: response.ok };
          });
        })
        .then(function (result) {
          var errors = extractFormErrors(result.html);

          if (formLooksSuccessful(result.html, errors) || (result.ok && errors.length === 0)) {
            finishSuccess();
            return;
          }

          if (errors.length > 0) {
            var duplicateEmail = errors.some(function (message) {
              return /already been taken|already subscribed|already exists/i.test(message);
            });

            if (duplicateEmail) {
              finishSuccess();
              return;
            }

            showFeedback('error', errors.join(' '));
            submitButton.disabled = false;
            return;
          }

          finishSuccess();
        })
        .catch(function () {
          showFeedback('error', 'Something went wrong. Please try again.');
          submitButton.disabled = false;
        });
    }

    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    // Escape and the Tab focus trap are handled on document while the modal is open
    // (onDocumentKeydown), so they work even when focus is on a non-focusable part of the card.

    root.querySelectorAll('[data-mmo-select]').forEach(function (button) {
      button.addEventListener('click', function () {
        goToStep2(button);
      });
    });

    if (backButton) {
      backButton.addEventListener('click', function () {
        resetFeedback();
        setStep(1);
        var selected = root.querySelector('.monthlees-modal-overlay__option--selected') || root.querySelector('[data-mmo-select]');
        if (selected) {
          window.requestAnimationFrame(function () {
            selected.focus();
          });
        }
      });
    }

    if (form) {
      form.addEventListener('submit', submitEmail);
    }

    if (designMode) {
      window.setTimeout(function () {
        armed = true;
        opened = false;
        tryOpen();
      }, 600);
      return;
    }

    if (isStorageBlocked()) {
      armed = false;
      return;
    }

    var hasTrigger = timeEnabled || scrollEnabled || exitEnabled;
    if (!hasTrigger) return;

    if (timeEnabled && timeDelay >= 0) {
      window.setTimeout(function () {
        if (armed) tryOpen('time');
      }, timeDelay);
    }

    if (scrollEnabled) {
      var scrollTicking = false;

      function checkScroll() {
        scrollTicking = false;
        if (!armed) return;

        var doc = document.documentElement;
        var scrollTop = window.pageYOffset || doc.scrollTop || 0;
        var scrollHeight = Math.max(doc.scrollHeight, document.body.scrollHeight) - window.innerHeight;

        if (scrollHeight <= 0) {
          tryOpen('scroll');
          return;
        }

        var pct = (scrollTop / scrollHeight) * 100;
        if (pct >= scrollPercent) {
          tryOpen('scroll');
        }
      }

      window.addEventListener(
        'scroll',
        function () {
          if (!scrollTicking) {
            scrollTicking = true;
            window.requestAnimationFrame(checkScroll);
          }
        },
        { passive: true }
      );

      checkScroll();
    }

    if (exitEnabled) {
      function onExitIntent(event) {
        if (!armed) return;
        if (event.clientY > 12) return;
        if (event.relatedTarget != null) return;
        tryOpen('exit');
      }

      document.documentElement.addEventListener('mouseout', onExitIntent);
      document.body.addEventListener('mouseleave', function (event) {
        if (!armed) return;
        if (event.clientY > 12) return;
        tryOpen('exit');
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-monthlees-modal-overlay]').forEach(initModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
